import { filterUndefined } from '@townsquare/filter-type';

import {
	ComparatorOperator,
	Content,
	Entity,
	FilterComparators,
	FilterDoc,
	FilterOperator,
	Operators,
	Resolver,
	SupportedFilters,
	SupportedFiltersTypes,
} from './types';

/**
 * Doc helpers
 */
const FALLBACK_OPERATOR = Operators.AND;
const FALLBACK_COMPARATOR = ComparatorOperator.EQUALS;

const createBaseDocEntity = (
	type: SupportedFilters,
	value: Content | number[],
	defaultResolverOperator?: Operators,
	comparator?: ComparatorOperator,
): Entity => {
	return {
		type,
		operator: defaultResolverOperator ?? FALLBACK_OPERATOR,
		model: Array.isArray(value) ? value : [value],
		comparator: comparator ?? FALLBACK_COMPARATOR,
	};
};

export const hasContentEntry = (doc: FilterDoc, type: SupportedFilters, entityIndex: number): boolean => {
	return !!doc.model.length && !!doc.model.find((entity, idx) => type === entity.type && idx === entityIndex);
};

/**
 * This function is responsible for adding a newly selected value in a specific filter to the overall TQL filter.
 */
export const appendContentEntry = (
	doc: FilterDoc,
	entityIndex: number,
	type: SupportedFilters,
	value: Content | number[],
	resolverOperators?: Operators[],
	comparators?: ComparatorOperator[],
): FilterDoc => {
	let newModel: Entity[];
	// If doc is empty or the current type doesnt exist yet, we need to add a new base doc entity for it.
	if (!hasContentEntry(doc, type, entityIndex)) {
		const operator = (resolverOperators ?? [])[0];
		const comparator = (comparators ?? [])[0];
		newModel = doc.model.concat(createBaseDocEntity(type, value, operator, comparator));
	} else if (typeof value === 'boolean') {
		/**
		 * For boolean values, if the entry already exists we want to REPLACE the existing filter.
		 *
		 * It doesn't make sense to have a filter of "starred = true OR starred = false", so if the current filter is to true and the user
		 * clicks false we should replace true with false.
		 */

		// Find our existing filter of this type and replace it with a new base doc entity
		newModel = doc.model.map((entity, idx) => {
			if (type === entity.type && idx === entityIndex) {
				const operator = (resolverOperators ?? [])[0];
				const comparator = (comparators ?? [])[0];
				return createBaseDocEntity(type, value, operator, comparator);
			}

			return entity;
		});
	} else {
		// Find our existing filter of this type and append the value and update the operator and comparator as appropriate
		newModel = doc.model.map((entity, idx) => {
			if (type === entity.type && idx === entityIndex) {
				entity.model = entity.model.concat(value);

				// Adding a check here to ensure the operator is set to a valid one.
				if (resolverOperators && !resolverOperators.includes(entity.operator)) {
					entity.operator = (resolverOperators ?? [])[0] ?? FALLBACK_OPERATOR;
				}
				// Ensure the comparator is set to a valid one.
				if (comparators && !comparators.includes(entity.comparator)) {
					entity.comparator = (comparators ?? [])[0] ?? FALLBACK_COMPARATOR;
				}
			}

			return entity;
		});
	}

	return {
		operator: doc.operator,
		model: newModel,
	};
};

export const removeContentEntry = (
	doc: FilterDoc,
	entityIndex: number,
	type: SupportedFilters,
	modelIndex: number,
): FilterDoc => {
	const newModel = doc.model
		.map((entity, idx) => {
			if (entity.type === type && idx === entityIndex) {
				const filteredModel = entity.model.filter((_value, index) => {
					return index !== modelIndex;
				});

				if (filteredModel.length === 0) {
					return;
				}

				return {
					...entity,
					model: filteredModel,
				};
			}
			return entity;
		})
		.filter(filterUndefined);

	return {
		operator: doc.operator,
		model: newModel,
	};
};

export const updateDocOperator = (doc: FilterDoc, newOperator: Operators): FilterDoc => {
	return {
		operator: newOperator,
		model: doc.model,
	};
};

export const updateOperatorEntry = (
	doc: FilterDoc,
	entityIndex: number,
	type: SupportedFilters,
	newOperator: Operators,
): FilterDoc => {
	const newModel = doc.model.map((entity, idx) => {
		if (entity.type === type && idx === entityIndex) {
			return {
				...entity,
				operator: newOperator,
			};
		}

		return entity;
	});

	return {
		operator: doc.operator,
		model: newModel,
	};
};

export const updateComparatorEntry = (
	doc: FilterDoc,
	entityIndex: number,
	type: SupportedFilters,
	newComparator: ComparatorOperator,
	newOperator?: Operators,
): FilterDoc => {
	const newModel = doc.model.map((entity, idx) => {
		if (entity.type === type && idx === entityIndex) {
			return {
				...entity,
				comparator: newComparator,
				operator: newOperator || entity.operator,
			};
		}

		return entity;
	});

	return {
		operator: doc.operator,
		model: newModel,
	};
};

export const findFilterComparatorOnResolver = (
	comparator: ComparatorOperator,
	resolver: Resolver | undefined,
): FilterComparators | undefined => {
	return resolver?.filterComparators.find(item => item.comparatorOption === comparator);
};

export const findFilterOperatorOrDefault = (
	operator: Operators,
	allowedOperators: FilterOperator[],
): Operators | undefined => {
	if (!allowedOperators.length) {
		return undefined;
	}
	return allowedOperators.find(option => option.operator === operator)?.operator || allowedOperators[0].operator;
};

/**
 * Determines if a resolver only supports OR operations by checking if all its filter comparators
 * have empty allowedOperators arrays, which indicates OR-only behavior.
 */
export const isOrOnlyResolver = (resolver: Resolver): boolean => {
	return resolver.filterComparators.every(
		comparator => comparator.allowedOperators.filter(operator => operator.operator !== Operators.OR).length === 0,
	);
};

/**
 * Determines if a resolver should be disabled in the "Add filter" dropdown.
 * A resolver should be disabled if:
 * 1. It only supports OR operations (isOrOnlyResolver)
 * 2. It's already present in the current filter document
 * This prevents users from adding AND relationships for OR-only filters.
 */
export const shouldDisableResolver = (resolver: Resolver, filterDoc: FilterDoc): boolean => {
	if (!isOrOnlyResolver(resolver)) {
		return false;
	}

	return filterDoc.model.some(entity => entity.type === resolver.type);
};

// Matches ISO 8601 date-only values produced by formatIsoLocalDate: YYYY-MM-DD
const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
// Matches ISO 8601 datetime values produced by formatIsoLocalDateTime: YYYY-MM-DDTHH:MM:SS
const ISO_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

// Checks if a entity has a valid value for the given type. Note: this must be modified if we change the format of the values
// Ideally both functions should be able to handle date and datetime values.
const entityHasValidValue = (entity: Entity): boolean => {
	if (entity.model.length === 0) {
		return false;
	}
	return entity.model.every(value => {
		if (typeof value !== 'string') {
			return false;
		}
		if (entity.type === SupportedFiltersTypes.TARGET_DATE && !ISO_DATE_ONLY_REGEX.test(value)) {
			return false;
		}
		if (entity.type === SupportedFiltersTypes.LAST_UPDATED && !ISO_DATETIME_REGEX.test(value)) {
			return false;
		}
		return true;
	});
};

// Validates if a pair of entities are a valid date range of the same type
export const isValidDateRange = (startDateEntity: Entity, endDateEntity?: Entity): boolean => {
	if (!endDateEntity) {
		return false;
	}
	// Check endDateEntity is a date type
	if (
		endDateEntity.type !== SupportedFiltersTypes.TARGET_DATE &&
		endDateEntity.type !== SupportedFiltersTypes.LAST_UPDATED
	) {
		return false;
	}
	// Check both entities share the same date range type
	if (startDateEntity.type !== endDateEntity.type) {
		return false;
	}
	// Check first entity is GTE and second entity is LTE
	if (startDateEntity.comparator !== ComparatorOperator.GTE || endDateEntity.comparator !== ComparatorOperator.LTE) {
		return false;
	}
	// Check model values match the expected date format for the field type
	if (!entityHasValidValue(startDateEntity) || !entityHasValidValue(endDateEntity)) {
		return false;
	}
	return true;
};
